import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { useErrorHandler } from 'react-error-boundary';

export type ExtractedAxiosError = {
  error: AxiosError<any>;
  status?: number;
  code?: string;
  rawText?: string | null;
  parsedJson?: any;
};

export function extractAxiosError(error: AxiosError<any>): ExtractedAxiosError {
  const rawText =
    (typeof error.response?.data === 'string' ? error.response.data : null) ||
    (error.request as any)?.responseText ||
    (error.request as any)?.response ||
    null;

  let parsedJson: any | undefined;
  if (rawText && typeof rawText === 'string') {
    try {
      parsedJson = JSON.parse(rawText);
    } catch {
      parsedJson = undefined;
    }
  }

  return {
    error,
    status: error.response?.status,
    code: error.code,
    rawText,
    parsedJson,
  };
}

type UseAxiosErrorReporterParams = {
  error?: AxiosError<any> | null;
  name: string;
  getUserMessage?: (info: ExtractedAxiosError) => string;
};

export const useAxiosErrorReporter = ({
  error,
  name,
  getUserMessage,
}: UseAxiosErrorReporterParams) => {
  const handleError = useErrorHandler<Error>();

  useEffect(() => {
    if (!error) return;

    const { parsedJson } = extractAxiosError(error);

    const staticErrorMessage =
      'Your request could not be completed successfully. Please try again later.';
    const errorMessage =
      (parsedJson && parsedJson.message) || error.message || '';
    const threadID = parsedJson?.thread_id
      ? `Thread ID - ${parsedJson?.thread_id}`
      : '';

    const errMessage = `${staticErrorMessage} \n ${errorMessage} \n ${threadID}`;

    handleError({
      name,
      message: errMessage,
    });
  }, [error, name, getUserMessage, handleError]);
};
